import { floatify } from 'lib/maths/mathUtils'
import { getAgentHubCommissionItemKey } from 'lib/agentHub/agentHubUtils'
import { CHECKOUT_ITEM_TYPE_TOUR_V2_EXPERIENCE } from 'constants/checkout'
import { isInstalmentFullyPaidOff } from './payInInstalmentsUtils'

/**
 * Base (`itemDiscount`) + incentive commission for a line.
 * Payment funding and discount allocation must use this, not `itemDiscount` alone.
 */
export function getAgentHubItemTotalCommissionAmount(
  item: Pick<App.AgentHubItemCommissionDetails, 'itemDiscount' | 'totalIncentiveAmount'> | undefined,
): number {
  if (!item) return 0
  return floatify((item.itemDiscount ?? 0) + (item.totalIncentiveAmount ?? 0))
}

export function getAgentHubTotals(order: App.Order, commission: number, isGrossPayment: boolean) {
  if (order.instalmentDetails) {
    const isPaid = isInstalmentFullyPaidOff(order.instalmentDetails)
    return { valueToSubtractFromNetTotal: !isPaid ? commission : 0, valueToAddToGrossTotal: isPaid ? commission : 0 }
  }
  if (order.reserveForZeroDetails) {
    const isPaid = !!order.reserveForZeroDetails.actualPaymentDate
    return { valueToSubtractFromNetTotal: !isPaid ? commission : 0, valueToAddToGrossTotal: isPaid ? commission : 0 }
  }
  if (order.depositDetails) {
    const isPaid = !!order.depositDetails.balance_paid_date
    return { valueToSubtractFromNetTotal: !isPaid ? commission : 0, valueToAddToGrossTotal: isPaid ? commission : 0 }
  }

  if (order.paymentScheduleDetails) {
    return { valueToSubtractFromNetTotal: commission, valueToAddToGrossTotal: 0 }
  }

  if (isGrossPayment) {
    return { valueToSubtractFromNetTotal: 0, valueToAddToGrossTotal: 0 }
  }

  return { valueToSubtractFromNetTotal: 0, valueToAddToGrossTotal: commission }
}

/**
 * Strips the leading transaction key from a commission key so lines that belong to the same
 * commission group (e.g. multiple rooms in a multi-room tour) resolve to a shared group id.
 */
export function getAgentHubCommissionGroupId(commissionKey: string, transactionKey?: string): string {
  return transactionKey && commissionKey.startsWith(`${transactionKey}-`)
    ? commissionKey.slice(transactionKey.length + 1)
    : commissionKey
}

/**
 * Namespaced by item type because a tour optional experience is keyed on its parent tour's offer id
 * (`purchasableOption.fkTourId`) — the same id the tour itself is keyed on.
 */
function getAgentHubCommissionGroupIdForItem(item: App.Checkout.AnyItem): string {
  return `${item.itemType}-${getAgentHubCommissionGroupId(getAgentHubCommissionItemKey(item), item.transactionKey)}`
}

/**
 * Tour optional experience discount lines are keyed on `fkExperienceId` rather than the cart item id
 * (see `getPromoItems` in lib/promo/promoMappers), so that id has to be indexed too.
 */
function getDiscountLineIdsForItem(item: App.Checkout.AnyItem): Array<string> {
  if (item.itemType === CHECKOUT_ITEM_TYPE_TOUR_V2_EXPERIENCE && item.purchasableOption.fkExperienceId) {
    return [item.itemId, item.purchasableOption.fkExperienceId]
  }
  return [item.itemId]
}

/**
 * Allocates each commission group's total across its discount lines, returning the commission
 * discount amount keyed by the line's index in `itemDiscounts`.
 */
export function allocateAgentHubCommissionByLineIndex(
  itemDiscounts: Array<App.Checkout.ItemDiscount>,
  cartItems: Array<App.Checkout.AnyItem>,
  itemsCommission: App.AgentHubCommission['itemsCommission'],
): Map<number, number> {
  const commissionByLineIndex = new Map<number, number>()

  const commissionByGroupId = new Map<string, number>()
  const countedCommissionKeys = new Set<string>()
  cartItems.forEach((item) => {
    const commissionKey = getAgentHubCommissionItemKey(item)
    // Items sharing a transaction key and offer id resolve to one commission entry: one quote read
    // twice, not two quotes, so counting it per item would double it. Deduping on the commission key
    // rather than the group id is deliberate — distinct entries in one group (two optional
    // experiences on the same tour) must still accumulate. This assumes items of different types
    // never share a commission key, which holds because optional experiences are created with their
    // own `uuidV4()` transaction key.
    if (countedCommissionKeys.has(commissionKey)) {
      return
    }
    const itemCommission = itemsCommission[commissionKey]
    if (!itemCommission) {
      return
    }
    countedCommissionKeys.add(commissionKey)
    const groupId = getAgentHubCommissionGroupIdForItem(item)
    commissionByGroupId.set(
      groupId,
      floatify((commissionByGroupId.get(groupId) ?? 0) + getAgentHubItemTotalCommissionAmount(itemCommission)),
    )
  })

  if (!commissionByGroupId.size) {
    return commissionByLineIndex
  }

  const groupIdByItemId = new Map<string, string>()
  cartItems.forEach((item) => {
    const groupId = getAgentHubCommissionGroupIdForItem(item)
    if (commissionByGroupId.has(groupId)) {
      getDiscountLineIdsForItem(item).forEach(lineId => groupIdByItemId.set(lineId, groupId))
    }
  })

  if (!groupIdByItemId.size) {
    return commissionByLineIndex
  }

  // Group the discount lines by commission group so each group's lines can share that group's
  // commission total proportionally to each line's discountable amount.
  const lineIndexesByGroupId = new Map<string, Array<number>>()
  itemDiscounts.forEach((itemDiscount, index) => {
    const groupId = itemDiscount.itemId ? groupIdByItemId.get(itemDiscount.itemId) : undefined
    if (!groupId) {
      return
    }
    const existing = lineIndexesByGroupId.get(groupId) ?? []
    existing.push(index)
    lineIndexesByGroupId.set(groupId, existing)
  })

  lineIndexesByGroupId.forEach((lineIndexes, groupId) => {
    const commissionTotal = commissionByGroupId.get(groupId)!
    const totalDiscountable = lineIndexes.reduce(
      (acc, index) => acc + (itemDiscounts[index].discountableTotal || 0),
      0,
    )
    let allocated = 0
    lineIndexes.forEach((lineIndex, position) => {
      const isLast = position === lineIndexes.length - 1
      if (isLast) {
        commissionByLineIndex.set(lineIndex, floatify(commissionTotal - allocated))
        return
      }
      const share = totalDiscountable > 0
        ? floatify(commissionTotal * ((itemDiscounts[lineIndex].discountableTotal || 0) / totalDiscountable))
        : floatify(commissionTotal / lineIndexes.length)
      commissionByLineIndex.set(lineIndex, share)
      allocated = floatify(allocated + share)
    })
  })

  return commissionByLineIndex
}
